import { configureStore, createAction, isRejected } from '@reduxjs/toolkit';
import type { ThunkAction, Action, ThunkDispatch, Middleware, AnyAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import { LogLevel } from 'services/logger.service';
import { appLogger } from 'components/app/_mediator/app-logger.slice';

import { reducers } from './store.map';
import { ParsedReduxError } from 'components/app/_helpers/app-errors.helper';

export type RootState = ReturnType<typeof reducers>;
export type AppDispatch = ThunkDispatch<RootState, unknown, AnyAction>;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;
export type ReduxError = AxiosError<{ error: string }>;

const errorMiddleware: Middleware<Record<string, unknown>, unknown, ThunkDispatch<unknown, unknown, AnyAction>> =
  ({ dispatch }) =>
  (next) =>
  (action: AnyAction) => {
    const result = next(action);
    if (isRejected(result)) {
      if (result.payload && typeof result.payload === 'object' && 'message' in result.payload) {
        const { message, extra, isInternalServerError } = result.payload as ParsedReduxError;
        if (!isInternalServerError) {
          dispatch(
            appLogger({
              level: LogLevel.Fatal,
              error: { name: result.type, message, stack: extra },
            })
          );
        }
      } else {
        dispatch(
          appLogger({
            level: LogLevel.Fatal,
            error: { name: result.type, message: 'Unhandled redux error' },
          })
        );
      }
      console.error({ type: result.type, payload: result.payload });
    }
    return result;
  };

const accessModalLogger: Middleware<Record<string, unknown>, unknown, ThunkDispatch<unknown, unknown, AnyAction>> =
  ({ dispatch, getState }) =>
  (next) =>
  (action: AnyAction) => {
    const result = next(action);

    if (action.type === 'projects/setAccessTypeModal') {
      const state = getState() as RootState;
      const projectsState = state.projects;

      const debugData = {
        newValue: action.payload,
        previousValue: projectsState.accessTypeModal,
        selectedProject: projectsState.selectedProject,
        selectedStory: projectsState.selectedStory,
        selectedFinalcut: projectsState.selectedFinalcut,
        selectedFile: projectsState.selectedFile,
        filesTableState: projectsState.filesTableState,
        storiesTableState: projectsState.storiesTableState,
        projectsTableState: projectsState.projectsTableState,
        finalCutsTableState: projectsState.finalCutsTableState,
      };

      dispatch(
        appLogger({
          level: LogLevel.Error,
          error: {
            name: 'DEBUG_setAccessTypeModal',
            message: JSON.stringify(debugData),
            stack: new Error().stack,
          },
        })
      );
    }

    return result;
  };
//

const resetStore = createAction('app/reset');
export const rootReducer = (state: RootState | undefined, action: ReturnType<typeof resetStore>) => {
  if (action.type === 'app/reset') state = undefined;
  return reducers(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(errorMiddleware, accessModalLogger),
});
